import { useEffect, useState } from "react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { toast } from "sonner";
import { Modal } from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { getLevelById, updateLevel } from "../../../api/pedagogicElement";
import { getAllLevelTypes } from "../../../api/settings";
import { Select } from "antd";
import withPermission from "../../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";

type UUID = string;

type Props = {
  levelID: UUID;
  show: boolean;
  onClose: () => void;
  onSaved?: () => void;
};

const EditLevelModal = ({ levelID, show, onClose, onSaved }: Props) => {
  const [allLevelTypes, setAllLevelTypes] = useState<any[]>([]);

  const createSchema = z.object({
    label: z.string().nonempty("Le libellé du niveau d'étude est requis"),
    cycle: z.string().nonempty("Veuillez sélectionner le cycle"),
    id_typeLevel: z
      .string()
      .nonempty("Veuillez sélectionner le type de niveau"),
    abbreviation: z.string().nonempty("L'abréviation est requis"),
  });

  type FormFields = z.infer<typeof createSchema>;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(createSchema),
  });

  const selectedLevelTypeId = watch("id_typeLevel");

  const syncAbbreviationWithTypeLevel = (typeLevelId?: string) => {
    const selectedTypeLevel = allLevelTypes.find(
      (lvl) => lvl.id === typeLevelId,
    );
    setValue("abbreviation", selectedTypeLevel?.label || "", {
      shouldValidate: true,
    });
  };

  //Traitement après validation du formulaire de modification d'un niveau
  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      //Envoi des datas pour modification vers le backend
      await updateLevel(data, levelID);

      reset(); //On réinitialise les champs du formulaire
      if (onSaved) onSaved(); // On fait un reload de la liste

      toast.success("Niveau d'étude mise à jour !"); //Toast de succès

      onClose(); // On ferme le modal
    } catch (error: any) {
      // Si erreur
      // Si erreur (Toast Error)
      toast.error(
        error.message ||
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  useEffect(() => {
    if (!show || !levelID) return;

    const fetchLevel = async () => {
      try {
        const [response, levelTypes] = await Promise.all([
          getLevelById(
            levelID,
            buildPermissionContext(Resource.LEVEL, Action.UPDATE),
          ),
          getAllLevelTypes(),
        ]);

        if (!response) {
          throw new Error("Niveau d'Étude introuvable");
        }

        setAllLevelTypes(levelTypes.data || []);

        reset({
          label: response.data.label,
          cycle: response.data.cycle,
          id_typeLevel:
            response.data.id_typeLevel || response.data.TypeLevel?.id || "",
          abbreviation: response.data.abbreviation,
        });
      } catch (error: any) {
        onClose();
        toast.error(
          error.message ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            "Une erreur s'est produit, Réessayer plus tard",
        );
      }
    };

    fetchLevel();
  }, [show, levelID, reset]);

  return (
    <>
      <Modal show={show} onHide={() => onClose()}>
        <Modal.Header closeButton>
          <Modal.Title>Modifier un Niveau d'Étude </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form
            id="academic-year-form"
            className="needs-validation"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row row-cols-xxl-5 row-cols-md-6">
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label">Cycle d'étude</label>
                  <select
                    {...register("cycle")}
                    aria-label="Default select example"
                    className={
                      errors.cycle ? "form-select is-invalid" : "form-select"
                    }
                  >
                    <option value={""}>Sélectionner</option>
                    <option value={"Licence"}>Licence</option>
                    <option value={"Master"}>Master</option>
                  </select>
                  {errors.cycle && (
                    <div className="invalid-feedback">
                      {errors.cycle.message}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Types de niveau
                  </label>
                  <input type="hidden" {...register("id_typeLevel")} />
                  <Select
                    showSearch
                    allowClear
                    size="large"
                    optionFilterProp="label"
                    removeIcon={<span style={{ fontSize: 12 }}>×</span>}
                    style={{ width: "100%" }}
                    getPopupContainer={(trigger) =>
                      trigger.parentElement as HTMLElement
                    }
                    dropdownStyle={{ zIndex: 2000 }}
                    placeholder="Sélectionner le type de niveau"
                    value={selectedLevelTypeId || undefined}
                    onChange={(value) => {
                      setValue("id_typeLevel", value || "", {
                        shouldValidate: true,
                      });
                      syncAbbreviationWithTypeLevel(value);
                    }}
                    onClear={() => {
                      setValue("id_typeLevel", "", { shouldValidate: true });
                      setValue("abbreviation", "", { shouldValidate: true });
                    }}
                  >
                    {allLevelTypes.map((lvl) => (
                      <Select.Option
                        key={lvl.id}
                        value={lvl.id}
                        label={`${lvl.label ?? ""}`}
                      >
                        {lvl.label}
                      </Select.Option>
                    ))}
                  </Select>
                  {errors.id_typeLevel && (
                    <div className="invalid-feedback d-block">
                      {errors.id_typeLevel.message}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Libellé
                  </label>
                  <input
                    {...register("label")}
                    type="text"
                    className={
                      errors.label ? "form-control is-invalid" : "form-control"
                    }
                    placeholder="Ex: Licence 1 ou Licence 2"
                  />
                  {errors.label && (
                    <div className="invalid-feedback">
                      {errors.label.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Abréviation
                  </label>
                  <input
                    {...register("abbreviation")}
                    type="text"
                    className={
                      errors.abbreviation
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Ex: L1 ou L2"
                  />
                  {errors.abbreviation && (
                    <div className="invalid-feedback">
                      {errors.abbreviation.message}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light me-3"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Annuler
          </button>

          {isSubmitting ? (
            <button
              className="btn btn-primary-light"
              type="button"
              disabled={false}
            >
              <span
                className="spinner-border spinner-border-sm align-middle me-1"
                role="status"
                aria-hidden="true"
              />
              Chargement...
            </button>
          ) : (
            <button
              type="submit"
              form="academic-year-form" // 🔗 liaison au formulaire
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              Enregistrer
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withPermission(
  EditLevelModal,
  [buildPermission(Resource.LEVEL, Action.UPDATE)],
  "masquer",
);
